import React, { memo, useState, useRef, useEffect } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { Input } from "@perceptron/ui/components/input";
import { Label } from "@perceptron/ui/components/label";
import { cn } from "@perceptron/utils/cn";

import { useAnnotationStore } from "../store/annotationStore";
import { EditorAnnotation } from "../store/annotationTypes";

interface AnnotationItemProps {
  annotation: EditorAnnotation;
  onDelete: (id: string) => void;
  onUpdateLabel: (id: string, newLabel: string) => void;
  onSelect: (id: string) => void;
}

export const AnnotationItem = memo(function AnnotationItem({
  annotation,
  onDelete,
  onUpdateLabel,
  onSelect,
}: AnnotationItemProps) {
  const { autoEditAnnotationId, clearAutoEditAnnotationId } = useAnnotationStore();
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if this annotation should auto-enter edit mode
  const shouldAutoEdit = autoEditAnnotationId === annotation.id;

  // Initialize state based on whether this is the auto-edit target
  const [isEditing, setIsEditing] = useState(shouldAutoEdit);

  // Track when editing started (initialized if auto-editing)
  const [editStartTime, setEditStartTime] = useState(() => Date.now());

  // Clear the auto-edit flag after using it
  useEffect(() => {
    if (shouldAutoEdit) {
      clearAutoEditAnnotationId();
    }
  }, [shouldAutoEdit, clearAutoEditAnnotationId]);

  const [editValue, setEditValue] = useState(annotation.label);
  const hoveredAnnotationId = useAnnotationStore((state) => state.hoveredAnnotationId);
  const setHoveredAnnotationId = useAnnotationStore((state) => state.setHoveredAnnotationId);
  const isHovered = hoveredAnnotationId === annotation.id;

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(annotation.id);
  };

  // Focus effect for both auto and manual edit
  useEffect(() => {
    if (isEditing && inputRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        });
      });
    }
  }, [isEditing]);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditStartTime(Date.now());
    setEditValue(annotation.label);
  };

  const handleSaveEdit = () => {
    if (editValue.trim() && editValue !== annotation.label) {
      onUpdateLabel(annotation.id, editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditValue(annotation.label);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Stop propagation to prevent global keyboard shortcuts from firing
    e.stopPropagation();

    if (e.key === "Enter") {
      handleSaveEdit();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const handleItemClick = () => {
    if (!isEditing) {
      onSelect(annotation.id);
    }
  };

  return (
    <li
      className={cn(
        "flex min-w-0 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-white",
        isHovered && "bg-zinc-800 text-white",
      )}
      onClick={handleItemClick}
      onMouseEnter={() => setHoveredAnnotationId(annotation.id)}
      onMouseLeave={() => setHoveredAnnotationId(null)}
    >
      {/* Content */}
      <div className="flex min-w-0 flex-1 items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {isEditing ? (
            <Input
              autoFocus
              className={cn(
                "h-6 flex-1 rounded-none border-0 border-b border-zinc-600 bg-transparent p-0 text-sm text-white shadow-none",
                "hover:border-b-zinc-500 focus:border-b-zinc-400 focus-visible:ring-0",
                "[&::selection]:bg-selection-background [&::selection]:text-white",
              )}
              placeholder={strings.annotationSidebar.editLabel}
              ref={inputRef}
              type="text"
              value={editValue}
              onBlur={() => {
                // Ignore blur if it happens too quickly after starting edit
                if (Date.now() - editStartTime < 200) {
                  console.log("[AnnotationItem] Ignoring blur because it happened too quickly after starting edit");
                  setTimeout(() => inputRef.current?.focus(), 0);
                  return;
                }
                console.log("[AnnotationItem] Saving edit");
                handleSaveEdit();
              }}
              onChange={(e) => setEditValue(e.target.value)}
              onFocus={(e) => e.target.select()}
              onKeyDown={handleKeyDown}
            />
          ) : (
            <Label
              className="block min-w-0 flex-1 cursor-pointer truncate text-sm font-normal hover:underline hover:underline-offset-4"
              title={annotation.label}
              onClick={handleEditClick}
            >
              {annotation.label}
            </Label>
          )}
        </div>

        <Button
          className="h-6 w-6 rounded-sm p-0 text-zinc-400 hover:bg-red hover:text-white"
          size="icon"
          title={strings.annotationSidebar.deleteAnnotation}
          variant="ghost"
          onClick={handleDeleteClick}
        >
          <Icon className="h-3 w-3" name="trash-2" />
        </Button>
      </div>
    </li>
  );
});
